/**
 * Where somebody's interest goes when they press the button.
 *
 * ## IT POSTS TO THE PRODUCT'S API, ON ONE PATH BUILT FOR IT
 *
 * `POST https://app.wendhq.com/api/public/interests` stores the enquiry in the
 * `interests` table, and it appears on the admin panel's Interests tab for the
 * sales desk to work. An earlier version of this file refused to post to the
 * API at all, and its reason still shapes the route: the API fronts real
 * customers' contact data behind row level security, and an unauthenticated
 * write from a marketing page must not widen what a stranger can reach. So the
 * route was built for exactly this and nothing more (`apps/api/src/modules/
 * interests`):
 *
 *   - it writes to a staff table with no tenant column and no policy, under its
 *     own bypass reason, so no tenant data is in reach of it;
 *   - its body is `.strict()` and cannot set a status, a source or an author;
 *   - it is rate-limited per address and per email;
 *   - it is opened cross-origin to this page's origins only, and **without
 *     credentials** — the browser sends no cookies, so the request carries no
 *     one's session even if a staff member is signed in elsewhere.
 *
 * ## WHICH ADDRESS
 *
 * `VITE_INTEREST_ENDPOINT` wins when it is set at build time. Otherwise a
 * production build posts to the live API and a development build to the
 * development API (`apps/api` in the wend-dev checkout, port 3101), whose
 * default `SITE_ORIGINS` already allows this dev server's origin.
 *
 * There is deliberately no path in which the button does nothing: a failure
 * says so and gives {@link CONTACT_EMAIL}, and the page shows that address
 * beside the form whatever happens.
 */

/** The address the page shows, and where a failed submission is pointed. */
export const CONTACT_EMAIL = 'hello@wendhq.com';

/** What the page asks for. Every field is the reader's own; none of it is kept in the browser. */
export interface InterestEnquiry {
  readonly name: string;
  readonly email: string;
  readonly organisation: string;
  readonly message: string;
  /**
   * The honeypot: rendered off-screen, never filled in by a person. The API
   * answers a submission that fills it exactly as it answers a real one, and
   * stores nothing.
   */
  readonly website: string;
}

/**
 * How the submission went, so the page can say the true thing. Only `sent`
 * promises a reply: it is the only outcome in which a row exists on our side.
 */
export type InterestOutcome =
  | { readonly kind: 'sent' }
  | {
      readonly kind: 'failed';
      readonly message: string;
      /** The API's refusal, by field, when it named one. */
      readonly problems?: Readonly<Record<string, string>>;
    };

/** What the page says for each field the API can refuse, in the page's voice. */
const FIELD_PROBLEM: Readonly<Record<string, string>> = {
  name: 'Check your name. It is too long or has characters we cannot store.',
  organisation: 'Check the company name. It is too long or has characters we cannot store.',
  email: 'That does not look like an email address we can reply to.',
  message: 'That message is too long, or has characters we cannot store. Shorten it a little.',
};

/** Field names from a 400's error envelope, mapped to the page's sentences. */
async function problemsFrom(response: Response): Promise<Record<string, string>> {
  const problems: Record<string, string> = {};
  try {
    const body: unknown = await response.json();
    const fieldErrors = (body as { error?: { fieldErrors?: unknown } }).error?.fieldErrors;
    if (!Array.isArray(fieldErrors)) return problems;
    for (const entry of fieldErrors) {
      const path = (entry as { path?: unknown }).path;
      if (typeof path === 'string' && FIELD_PROBLEM[path] !== undefined) problems[path] = FIELD_PROBLEM[path];
    }
  } catch {
    // An unreadable body is a refusal with no field named; the caller says so.
  }
  return problems;
}

const LIVE_ENDPOINT = 'https://app.wendhq.com/api/public/interests';
const DEV_ENDPOINT = 'http://localhost:3101/api/public/interests';

/* Resolved when the form is sent, not when the module loads: slice 71's site
   suite renders the whole page under node, where `import.meta.env` does not
   exist, and the page must render there without ever reaching this line. In a
   Vite build the result is the same either way — both values are constants. */
function endpoint(): string {
  const configured = (import.meta.env.VITE_INTEREST_ENDPOINT ?? '').trim();
  if (configured !== '') return configured;
  return import.meta.env.PROD ? LIVE_ENDPOINT : DEV_ENDPOINT;
}

/** A fresh, empty enquiry. Exported so the page and its probes start from one shape. */
export function emptyEnquiry(): InterestEnquiry {
  return { name: '', email: '', organisation: '', message: '', website: '' };
}

/**
 * What is missing, by field.
 *
 * Deliberately thin: a name, something that could be an email address, and an
 * organisation. The message is optional — somebody who only wants a call
 * should not have to compose a paragraph to ask for one.
 *
 * The email test here is `@` with something either side and a dot after it —
 * **looser** than the API's (`emailSchema`, zod's `.email()`), on purpose: it
 * catches the obvious slip before a round trip, and the API's own refusal is
 * mapped back onto the field it names (see {@link submitInterest}), so an
 * address the page let through and the API did not is still shown against the
 * email box rather than as a generic failure.
 */
export function enquiryProblems(enquiry: InterestEnquiry): Readonly<Record<string, string>> {
  const problems: Record<string, string> = {};

  if (enquiry.name.trim() === '') problems['name'] = 'Tell us who you are.';
  if (enquiry.organisation.trim() === '') problems['organisation'] = 'Which company?';

  const email = enquiry.email.trim();
  if (email === '') problems['email'] = 'We need somewhere to reply.';
  else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) problems['email'] = 'That does not look like an email address.';

  return problems;
}

/**
 * Deliver it.
 *
 * The caller is expected to have run {@link enquiryProblems} first; this
 * refuses anyway, because a function that posts whatever it is handed is one
 * validation bug away from being a relay.
 */
export async function submitInterest(enquiry: InterestEnquiry): Promise<InterestOutcome> {
  if (Object.keys(enquiryProblems(enquiry)).length > 0) {
    return { kind: 'failed', message: 'Some of the fields above still need filling in.' };
  }

  try {
    const response = await fetch(endpoint(), {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        name: enquiry.name.trim(),
        email: enquiry.email.trim(),
        organisation: enquiry.organisation.trim(),
        message: enquiry.message.trim(),
        website: enquiry.website,
      }),
      /* No cookies, ever: the API's rule for this path allows no credentials,
         and an enquiry has no business carrying anybody's session. */
      credentials: 'omit',
    });

    if (response.status === 429) {
      return {
        kind: 'failed',
        message: `We have already had a few of these from you in the last hour. Write to ${CONTACT_EMAIL} and we will pick it up.`,
      };
    }

    if (response.status === 400) {
      const problems = await problemsFrom(response);
      if (Object.keys(problems).length > 0) {
        return { kind: 'failed', message: 'Something above needs a second look.', problems };
      }
    }

    if (!response.ok) {
      /* The API's own words are not shown: they are written for a developer,
         and the reader cannot act on them. The address below is something they
         can act on. */
      return { kind: 'failed', message: `That did not go through. Write to ${CONTACT_EMAIL} instead.` };
    }

    return { kind: 'sent' };
  } catch {
    return { kind: 'failed', message: `That did not go through. Write to ${CONTACT_EMAIL} instead.` };
  }
}
